import React from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface StatCardProps {
  title: string;
  value: string | number;
  suffix?: string;
  percentageChange: number;
  comparisonLabel: string;
  icon: React.ReactNode;
  loading: boolean;
  onClick?: () => void;
  /** Kept for the call sites that still pass it; the card reads its colours from the theme. */
  semanticColors?: unknown;
}

/**
 * One number, its direction, and what it is being compared with.
 *
 * The change is the only colour on the card — green up, red down — and a
 * change of exactly zero says nothing rather than showing a grey zero.
 */
export const StatCard = ({
  title,
  value,
  suffix = '',
  percentageChange,
  comparisonLabel,
  icon,
  loading,
  onClick,
}: StatCardProps) => {
  const isPositive = percentageChange >= 0;
  const isNeutral = percentageChange === 0;

  return (
    <Card
      onClick={onClick}
      className={cn('relative justify-between gap-0 py-4', onClick && 'cursor-pointer transition-colors hover:bg-accent/50')}
    >
      <CardContent className="px-4">
        {icon && (
          <span className="absolute end-4 top-4 text-muted-foreground [&_svg]:size-4">{icon}</span>
        )}

        <p className="mb-2 text-sm text-muted-foreground">{title}</p>

        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-3xl leading-none font-medium tracking-tight whitespace-nowrap">
              {value}
              {suffix}
            </span>

            {!isNeutral && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 text-xs font-medium',
                  isPositive ? 'text-success' : 'text-destructive',
                )}
              >
                {isPositive ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
                {Math.abs(percentageChange)}%
              </span>
            )}
          </div>
        )}

        {!loading && comparisonLabel && (
          <p className="mt-1.5 text-xs text-muted-foreground">{comparisonLabel}</p>
        )}
      </CardContent>
    </Card>
  );
};
